import { Button, Center, Group, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { IconFigmaArrowRight, IconFigmaCheck } from '@/assets/icons';
import { AuthCard } from '@/features/auth/components/AuthCard';
import { brandTextStyle } from '@/features/auth/components/authFieldStyles';

interface OrganisationsConnectedCardProps {
  yourOrgName: string;
  partnerOrgName: string;
}

const infoRowStyle = {
  borderRadius: 6,
  backgroundColor: 'var(--color-bg-surface-2)',
  border: '1px solid var(--color-border-default)'
};

const infoLabelStyle = { color: 'var(--color-text-muted)', letterSpacing: 0.4 };

export const OrganisationsConnectedCard = ({
  yourOrgName,
  partnerOrgName
}: OrganisationsConnectedCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <AuthCard p={{ base: 24, md: 32 }} w={{ base: '100%', md: 480 }}>
      <Stack align="center" gap={24}>
        <Center
          w={56}
          h={56}
          style={{
            borderRadius: 12,
            backgroundColor: 'var(--color-status-success-soft)',
            color: 'var(--color-status-success)'
          }}
        >
          <IconFigmaCheck size={24} />
        </Center>
        <Stack align="center" gap={8}>
          <Title order={2} fz={24} fw={700} ta="center" style={brandTextStyle}>
            {t('connect.successTitle')}
          </Title>
          <Text fz={13} ta="center" style={{ color: 'var(--color-text-secondary)' }}>
            {t('connect.successSubtitle', { partner: partnerOrgName })}
          </Text>
        </Stack>

        <Stack gap={8} w="100%">
          <Group justify="space-between" px={16} py={10} style={infoRowStyle}>
            <Text fz={11} fw={600} tt="uppercase" style={infoLabelStyle}>
              {t('connect.successYourOrgLabel')}
            </Text>
            <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
              {yourOrgName}
            </Text>
          </Group>
          <Group justify="space-between" px={16} py={10} style={infoRowStyle}>
            <Text fz={11} fw={600} tt="uppercase" style={infoLabelStyle}>
              {t('connect.successPartnerLabel')}
            </Text>
            <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
              {partnerOrgName}
            </Text>
          </Group>
          <Group justify="space-between" px={16} py={10} style={infoRowStyle}>
            <Text fz={11} fw={600} tt="uppercase" style={infoLabelStyle}>
              {t('connect.successStatusLabel')}
            </Text>
            <Group
              gap={6}
              wrap="nowrap"
              px={10}
              py={4}
              style={{ borderRadius: 999, backgroundColor: 'var(--color-status-success-soft)' }}
            >
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-status-success)'
                }}
              />
              <Text fz={11} fw={600} style={{ color: 'var(--color-status-success)' }}>
                {t('connect.successStatusActive')}
              </Text>
            </Group>
          </Group>
        </Stack>

        <Button
          fullWidth
          rightSection={<IconFigmaArrowRight size={15} />}
          onClick={() => navigate('/login')}
        >
          {t('connect.successContinue')}
        </Button>
      </Stack>
    </AuthCard>
  );
};
